import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * What the user asked for. `system` defers to the OS; the other two are an
 * explicit override that outranks it.
 */
export type ThemePreference = 'light' | 'dark' | 'system';

/** What actually gets painted, once `system` has been resolved. */
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'cr-chat-theme';
export const THEME_DARK_CLASS = 'theme-dark';

/**
 * Routes that are ALWAYS painted light, whatever the preference resolves to
 * (CHAT-858).
 *
 * These are the pre-auth, registration and onboarding surfaces. Three reasons
 * they are pinned rather than themed:
 *
 *  1. They are reached with NO STORED PREFERENCE. A brand-new user has never
 *     seen the Portal, so `cr-chat-theme` is unset — and if the default ever
 *     becomes `system` (CHAT-925 phase 3), an OS-dark visitor would land on
 *     them in dark.
 *  2. They were NOT AUDITED by the sweep. CHAT-858 did touch the stepwizard,
 *     but only in passing: ~198 hex declarations still sit under /setup and
 *     /dialogs, the densest residue left anywhere in the app. Dark there is
 *     not "themed", it is unstyled.
 *  3. There is no way OUT of it: the appearance control lives in the app
 *     header, which none of these routes render. A user who landed in a broken
 *     dark onboarding flow could not switch back.
 *
 * `/dialogs` is pinned for a DIFFERENT reason, and it is the one exception to
 * point 3: it is an in-app route, so it does render the header. The Dialog
 * Builder is a jsPlumb canvas whose chrome (sticky header, canvas toolbar, node
 * sidebar, right panel) is painted by a 65-literal stylesheet built around a
 * light canvas, and half-theming a diagramming surface is worse than not
 * theming it. Pinned for v1 (CHAT-858 §3) until it gets its own pass. The
 * appearance control hides itself on pinned routes, so the pin is not silently
 * inert -- see HeaderComponent.showAppearance().
 *
 * NOTE the prefix rule below is what keeps this pin narrow: `/dialogs` matches
 * `/dialogs` and `/dialogs/...` but NOT `/dialogs2` (the Dialog IDE) or
 * `/dialog-messages`, both of which are left free to theme.
 *
 * Matched as path PREFIXES against the URL without its query or fragment, so
 * `/setup/step3` and `/success?x=1` are both covered.
 */
export const FORCED_LIGHT_ROUTES: readonly string[] = [
  '/setup',        // onboarding stepwizard
  '/register',     // self-service sign-up
  '/maintenance',
  '/success',
  '/fail',
  '/404',
  '/500',
  '/auth-callback',
  '/dialogs',      // Dialog Builder canvas (jsPlumb) -- see above
];

/** True when `url` is one of the always-light routes. */
export function isForcedLightRoute(url: string): boolean {
  const path = (url || '').split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  return FORCED_LIGHT_ROUTES.some((r) => path === r || path.startsWith(r + '/'));
}

/**
 * Light/dark control for the Chat Portal admin shell (CHAT-853).
 *
 * Resolves a three-way preference (light / dark / system) down to a single
 * `body.theme-dark` class, which is the switch the semantic token layer keys
 * off (see _semantic-tokens.scss). Toggling that class re-resolves every
 * var(--…) consumer at computed-value time, so the shell re-themes with no
 * rebuild and no second stylesheet bundle.
 *
 * ── THIS IS NOT A THEME SWITCHER ────────────────────────────────────────────
 * Two palettes, that is all. Chat has eight legacy theme-a..h.scss files, but
 * only theme-a is compiled into the build and there is no mechanism to swap
 * between them. Nothing here reintroduces one.
 *
 * ── WHY THE DEFAULT IS `light` AND NOT `system` ─────────────────────────────
 * Still deliberate, still the most important decision in this file — but the
 * reason has changed, so do not read the old one into it.
 *
 * The OLD reason was coverage: only the app shell consumed tokens, so a
 * `system` default would have given an OS-dark admin a dark shell wrapped
 * around ~124 light page-level components. That is fixed. CHAT-858 (#294)
 * swept the page components onto semantic tokens and CHAT-915 (#323/#325)
 * tokenised the shared substrate beneath them, and on the current tree 78 of
 * the 110 component stylesheets read var(--…); exactly one stylesheet on a
 * themeable route is still untokenized. Dark reaches the page body now.
 *
 * Three reasons the default stays `light` anyway:
 *
 *  1. IT IS A ROLLOUT DECISION, NOT A CAPABILITY ONE. CHAT-925 phase 2 (#328)
 *     ungated the Appearance control for every admin and explicitly left the
 *     default alone: opting in is a choice an admin makes, not a change that
 *     arrives under them. Flipping to `system` is phase 3 and is a separate
 *     sign-off.
 *  2. DARK IS COHERENT, NOT AUDITED. ~199 hex declarations survive across 36
 *     themeable component stylesheets — status chips, category badges,
 *     operator presence dots, a handful of semantic state colours with no
 *     token yet — plus the open CHAT-915 items (ink contrast, brand-var
 *     consistency). None of them paints a light surface in dark, which is the
 *     failure that mattered; all of them are reasons to be chosen rather than
 *     inherited.
 *  3. FORCED_LIGHT_ROUTES ONLY COVERS THE ROUTES IT LISTS. The pin above is
 *     what makes an unset preference safe on the pre-auth and canvas surfaces,
 *     and it is current — but it is a list, and a `system` default would put
 *     every unlisted route in front of an OS-dark user who never chose dark.
 *
 * The `system` branch itself is fully built and tested and needs no further
 * work; what it is waiting on is the phase-3 decision, not more sweeping.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService implements OnDestroy {

  /**
   * See the class comment: `light`, not `system`. Dark IS coherent beyond the
   * shell now — this holds as a rollout decision (CHAT-925 phase 3), so that
   * dark is opted into rather than inherited from the OS.
   */
  static readonly DEFAULT_PREFERENCE: ThemePreference = 'light';

  private readonly preference$ = new BehaviorSubject<ThemePreference>(ThemeService.DEFAULT_PREFERENCE);
  private readonly resolved$ = new BehaviorSubject<ResolvedTheme>('light');

  private mediaQuery: MediaQueryList | null = null;
  private mediaListener: ((e: MediaQueryListEvent) => void) | null = null;

  /**
   * Set while the active route is one of FORCED_LIGHT_ROUTES.
   *
   * Deliberately NOT written to storage: it is a property of where the user
   * currently is, not something they chose, so leaving an onboarding page must
   * restore whatever their real preference was.
   */
  private forcedLight = false;

  /** The user's stored choice, including `system`. */
  get preference(): ThemePreference {
    return this.preference$.value;
  }

  /** What is actually painted right now. */
  get resolvedTheme(): ResolvedTheme {
    return this.resolved$.value;
  }

  readonly preferenceChanges: Observable<ThemePreference> = this.preference$.asObservable();
  readonly themeChanges: Observable<ResolvedTheme> = this.resolved$.asObservable();

  /**
   * Read the stored preference and paint. Call once during app bootstrap.
   *
   * Idempotent, so a second call (tests, hot reload) is harmless.
   */
  initialize(): void {
    this.preference$.next(this.readStoredPreference());
    this.watchSystemPreference();
    this.apply();
  }

  /**
   * Pin the theme to light for the current route, or release it.
   *
   * Called by the router subscription in AppComponent. Idempotent, and it
   * repaints only when the flag actually moves, so it is safe to call on every
   * navigation.
   */
  setForcedLight(forced: boolean): void {
    if (this.forcedLight === forced) {
      return;
    }
    this.forcedLight = forced;
    this.apply();
  }

  /** Whether the current route is pinned to light. */
  get isForcedLight(): boolean {
    return this.forcedLight;
  }

  /** Record an explicit choice and repaint. */
  setPreference(preference: ThemePreference): void {
    this.preference$.next(preference);
    this.writeStoredPreference(preference);
    this.apply();
  }

  /**
   * Flip between light and dark.
   *
   * Resolves `system` first, so the toggle always moves away from what the user
   * is currently looking at rather than appearing to do nothing.
   */
  toggle(): void {
    this.setPreference(this.resolvedTheme === 'dark' ? 'light' : 'dark');
  }

  /**
   * Collapse the preference to what should actually be painted.
   *
   * The forced-light pin outranks BOTH an explicit preference and the OS: on
   * those routes there is no appearance control to undo it with, so honouring
   * a stored `dark` there would strand the user on an unstyled page.
   */
  resolve(preference: ThemePreference = this.preference): ResolvedTheme {
    if (this.forcedLight) {
      return 'light';
    }
    if (preference === 'system') {
      return this.prefersDark() ? 'dark' : 'light';
    }
    return preference;
  }

  ngOnDestroy(): void {
    this.unwatchSystemPreference();
  }

  // ── internals ────────────────────────────────────────────────────────────

  /** Put the resolved theme on <body> and announce it. */
  private apply(): void {
    const resolved = this.resolve();
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.toggle(THEME_DARK_CLASS, resolved === 'dark');
    }
    if (resolved !== this.resolved$.value) {
      this.resolved$.next(resolved);
    }
  }

  private prefersDark(): boolean {
    return !!this.mediaQuery?.matches;
  }

  /**
   * Track the OS setting so a `system` preference follows it live.
   *
   * The listener is attached regardless of the current preference: the user may
   * switch to `system` later, and re-subscribing at that point would miss a
   * change that happened in between. apply() ignores the OS unless the
   * preference is actually `system`.
   */
  private watchSystemPreference(): void {
    if (this.mediaQuery || typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.mediaListener = () => {
      if (this.preference === 'system') {
        this.apply();
      }
    };
    // addEventListener is the current API; addListener is kept for older
    // WebKit, which is still the fallback path on some managed macOS fleets.
    if (typeof this.mediaQuery.addEventListener === 'function') {
      this.mediaQuery.addEventListener('change', this.mediaListener);
    } else if (typeof (this.mediaQuery as any).addListener === 'function') {
      (this.mediaQuery as any).addListener(this.mediaListener);
    }
  }

  private unwatchSystemPreference(): void {
    if (!this.mediaQuery || !this.mediaListener) {
      return;
    }
    if (typeof this.mediaQuery.removeEventListener === 'function') {
      this.mediaQuery.removeEventListener('change', this.mediaListener);
    } else if (typeof (this.mediaQuery as any).removeListener === 'function') {
      (this.mediaQuery as any).removeListener(this.mediaListener);
    }
    this.mediaQuery = null;
    this.mediaListener = null;
  }

  /**
   * localStorage access is wrapped because it throws outright in Safari private
   * mode and when a group policy blocks site data — an admin should get the
   * default theme in that case, not a white screen.
   */
  private readStoredPreference(): ThemePreference {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    } catch {
      /* storage unavailable — fall through to the default */
    }
    return ThemeService.DEFAULT_PREFERENCE;
  }

  private writeStoredPreference(preference: ThemePreference): void {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
      /* storage unavailable — the preference simply will not persist */
    }
  }
}
